/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Off-screen sparkline rendered for the Stats home-screen widget. Home-screen
 * widgets cannot run a live chart, so the app rasterizes this to a PNG (via
 * `WidgetChartHost` + react-native-view-shot) and hands the image to the native
 * widget. Colors mirror `app/config/themes.js` so the widget matches the app in
 * both light and dark, and the `forceDark` prop lets the host render both
 * variants at once.
 */

import React from 'react';
import { View } from 'react-native';
import { LineChart } from 'react-native-chart-kit';

import { lightTheme, darkTheme } from '../../config/themes';

/** Fixed widget-chart geometry (medium-widget friendly). */
export const WIDGET_CHART_WIDTH = 300;
export const WIDGET_CHART_HEIGHT = 96;

/**
 * @param {{ series: number[], forceDark?: boolean }} props
 * @returns {React.ReactElement}
 */
const WidgetChartCanvas = ({ series = [], forceDark = false }) => {
    const theme = forceDark ? darkTheme : lightTheme;
    const data = Array.isArray(series) && series.length > 0 ? series : [0, 0];

    const chartConfig = {
        backgroundGradientFrom: theme.colors.card,
        backgroundGradientTo: theme.colors.card,
        // Happy orange brand line.
        color: (opacity = 1) => `rgba(249, 115, 22, ${opacity})`,
        labelColor: () => 'rgba(0,0,0,0)',
        strokeWidth: 2,
        decimalPlaces: 0,
        propsForBackgroundLines: { stroke: 'transparent' },
        propsForDots: { r: '0' },
        fillShadowGradientFrom: '#F97316',
        fillShadowGradientTo: theme.colors.card,
        fillShadowGradientOpacity: 0.25,
    };

    return (
        <View
            collapsable={false}
            style={{ backgroundColor: theme.colors.card, borderRadius: 12, overflow: 'hidden' }}
        >
            <LineChart
                data={{ labels: [], datasets: [{ data }] }}
                width={WIDGET_CHART_WIDTH}
                height={WIDGET_CHART_HEIGHT}
                chartConfig={chartConfig}
                bezier
                withDots={false}
                withInnerLines={false}
                withOuterLines={false}
                withHorizontalLabels={false}
                withVerticalLabels={false}
                withShadow
                style={{ paddingRight: 0, marginVertical: 0 }}
            />
        </View>
    );
};

export default WidgetChartCanvas;
